import { uniqBy } from 'lodash-es'
import { NEWSSTATE } from '.'
import {
    NewsActionsOptions,
    NewsInfo,
    NewsSearchType,
    NewsUserActions,
    NewsUserSettings,
    NewsViewSelectionOptions,
    UpdateNewsOptions,
} from '../libraries/mfc/MfcJsTypes'
import MfcNewsFeed from '../libraries/mfc/MfcNewsFeed'
import { locales } from '../locales/en'
import { safeArr } from '../utilities/safe'
import { pubSubNews } from './pubSub'
import { AppRouter } from './state.router'
import { registerTimer } from './state.timers'

let newsFeedClient: MfcNewsFeed = null

export const NEWS_REFRESH_MS =
    process.env.NODE_ENV === 'development' ? 20000 : 45000

/**
 * Get or create the MfcNewsFeed instance
 * (safe to call multiple times, returns existing instance if available)
 */
export const getNewsFeedClient = (): MfcNewsFeed => {
    if (typeof window === 'undefined') {
        return
    }
    if (!newsFeedClient) {
        newsFeedClient = new MfcNewsFeed()
    }
    return newsFeedClient
}

export const updateNewsSelection = (selectionType: string) => {
    // remove search queries
    NEWSSTATE.news.searchType = undefined
    NEWSSTATE.news.searchQuery = undefined

    const client = getNewsFeedClient()
    client.search_key = undefined
    client.search_string = undefined

    // update query
    const options: NewsViewSelectionOptions = {
        online_only: client.online_only,
        selection: selectionType,
    }
    client.changeViewSelection(options)
}

export const postNews = (
    message: string,
    onDone?: (posted: boolean) => void,
) => {
    const client = getNewsFeedClient()
    const data = {
        message: message,
    }
    const options = {
        callback: (result) => {
            onDone?.(Boolean(result))
            if (!result) return
            client.changeViewSelection({
                online_only: client.online_only,
                selection: 'my_news',
                callback: (nextStatusMessage) => {
                    NEWSSTATE.news.statusMessage = nextStatusMessage.message
                    getNews()
                },
            })
        },
    }
    client.postNews(data, options)
}

export const deleteNewsItem = (postId: number) => {
    const client = getNewsFeedClient()
    const options = {
        id: postId,
        callback: (_args) => {},
    }
    client.deleteNews(options)
}

export const disableOnlineOnly = () => {
    const client = getNewsFeedClient()
    if (!client.online_only) return false
    client.changeViewSelection({ online_only: false })
    NEWSSTATE.news.onlineOnly = false
    pubSubNews.emit('updateNews', NEWSSTATE.news)
    return true
}

export const toggleOnlineOnly = () => {
    const client = getNewsFeedClient()
    const nextValue = !client.online_only
    const options: NewsViewSelectionOptions = {
        online_only: nextValue,
        selection: client.view_selection,
    }
    client.changeViewSelection({
        ...options,
    })
    NEWSSTATE.news.onlineOnly = nextValue
    pubSubNews.emit('updateNews', NEWSSTATE.news)
    getNews()
    return nextValue
}

export const updateSettings = (
    type: NewsUserSettings,
    enable: boolean | number,
) => {
    const client = getNewsFeedClient()
    client.putOption({ key: type, value: enable })
}

export const userAction = (
    type: NewsUserActions,
    userId: number,
    removeFromList = false,
) => {
    if (NEWSSTATE.news.pendingUserIds.includes(userId)) return

    const key = type === 'hidden' ? 'hidden' : 'subscriptions_to'
    const lists: NewsInfo['lists'] = NEWSSTATE.news.information?.lists ?? {}
    const list = safeArr(lists[key]?.list)
    const listed = list.some((entry) => entry.user_id === userId)
    const user = safeArr(lists.subscriptions_from?.list).find(
        (entry) => entry.user_id === userId,
    ) ?? { user_id: userId, ...NEWSSTATE.news.users?.[userId] }
    if (listed === removeFromList) {
        NEWSSTATE.news.information = {
            ...NEWSSTATE.news.information,
            lists: {
                ...lists,
                [key]: {
                    count: (lists[key]?.count ?? 0) + (removeFromList ? -1 : 1),
                    list: removeFromList
                        ? list.filter((entry) => entry.user_id !== userId)
                        : list.concat(user),
                },
            },
        }
    }
    NEWSSTATE.news.pendingUserIds = NEWSSTATE.news.pendingUserIds.concat(userId)
    pubSubNews.emit('updateNews', NEWSSTATE.news)

    const client = getNewsFeedClient()
    const options: NewsActionsOptions = {
        key: type,
        value: userId,
    }
    client[removeFromList ? 'deleteOption' : 'putOption']({
        ...options,
        callback: (result) => {
            NEWSSTATE.news.pendingUserIds =
                NEWSSTATE.news.pendingUserIds.filter((id) => id !== userId)
            if (result === undefined) {
                NEWSSTATE.news.actionStatus = {
                    error: true,
                    message: locales.news.yourNews.actionFailed,
                }
                getInfo()
            }
            pubSubNews.emit('updateNews', NEWSSTATE.news)
        },
    })
}

export const search = (type: NewsSearchType, query: string) => {
    if (!query || !type) return
    if (
        NEWSSTATE.news.isLoading &&
        NEWSSTATE.news.searchType === type &&
        NEWSSTATE.news.searchQuery === query
    ) {
        return
    }

    NEWSSTATE.news.searchType = type
    NEWSSTATE.news.searchQuery = query

    // Update existing instance with search parameters
    const client = getNewsFeedClient()
    client.search_key = type
    client.search_string = query
    if (type === 'username') disableOnlineOnly()
    client.changeViewSelection({})
    getNews()
}

export const clearSearch = () => {
    NEWSSTATE.news.searchType = undefined
    NEWSSTATE.news.searchQuery = undefined

    const client = getNewsFeedClient()
    client.search_key = undefined
    client.search_string = undefined
    // reset back to default view if search is cleared
    client.changeViewSelection({})
    getNews()
}

const NEW_ITEM_HIGHLIGHT_MS = 10000
const refresh = {
    request: 0,
    highlightTimer: undefined as ReturnType<typeof setTimeout>,
}

const markNewItems = (ids: Array<number>) => {
    if (!ids.length) return
    NEWSSTATE.news.newItemIds = ids
    clearTimeout(refresh.highlightTimer)
    refresh.highlightTimer = setTimeout(() => {
        NEWSSTATE.news.newItemIds = []
        pubSubNews.emit('updateNews', NEWSSTATE.news)
    }, NEW_ITEM_HIGHLIGHT_MS)
}

export const getNews = ({
    silent = false,
    ...options
}: UpdateNewsOptions & { silent?: boolean } = {}) => {
    if (typeof window === 'undefined') return
    if (silent && (NEWSSTATE.news.isLoading || NEWSSTATE.news.isRefreshing)) {
        return
    }

    refresh.request += 1
    const requestId = refresh.request
    const client = getNewsFeedClient()
    if (silent) {
        NEWSSTATE.news.isRefreshing = true
    } else {
        NEWSSTATE.news.isLoading = true
        NEWSSTATE.news.isRefreshing = false
        if (NEWSSTATE.news.newItemIds.length) NEWSSTATE.news.newItemIds = []
    }
    pubSubNews.emit('updateNews', NEWSSTATE.news)
    const knownIds = new Set(NEWSSTATE.news.newsItems.map((item) => item.id))

    client.getNews({
        ...options,
        callback: (result) => {
            if (requestId !== refresh.request) return
            NEWSSTATE.news.isLoading = false
            NEWSSTATE.news.isRefreshing = false
            if (result) {
                client.news = uniqBy(client.news, 'id')
                if (silent && knownIds.size) {
                    markNewItems(
                        client.news
                            .filter((item) => !knownIds.has(item.id))
                            .map((item) => item.id),
                    )
                }
                NEWSSTATE.news.newsItems = client.news
                NEWSSTATE.news.users = { ...client.users }
                NEWSSTATE.news.viewSelection = client.view_selection
                NEWSSTATE.news.searchQuery = client.search_string
                NEWSSTATE.news.searchType = client.search_key
            }
            pubSubNews.emit('updateNews', NEWSSTATE.news)
        },
    })
}

export const clearNewsActionStatus = () => {
    NEWSSTATE.news.actionStatus = undefined
    pubSubNews.emit('updateNews', NEWSSTATE.news)
}

export const getInfo = () => {
    if (typeof window === 'undefined') return
    getNewsFeedClient().getInfo({
        callback: (information: NewsInfo) => {
            if (!information) return
            NEWSSTATE.news.information = information
            pubSubNews.emit('updateNews', NEWSSTATE.news)
        },
    })
}

export const loadMore = () => {
    if (NEWSSTATE.news.isLoading) return
    const client = getNewsFeedClient()
    client.offset++
    getNews({ show_more: 1 })
}

if (typeof window !== 'undefined') {
    registerTimer(
        'news',
        () => {
            if (AppRouter.route.visiblePage !== 'news') {
                return
            }
            getNews({
                silent: true,
                ...(NEWSSTATE.news.searchQuery === undefined
                    ? { update_only: 1 }
                    : {}),
            })
        },
        NEWS_REFRESH_MS,
    )
}
